import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { HandledScrapsDialog } from '../../components/handled-scraps-dialog/handledScrapsDialog.tsx';
import { LinkButton } from '../../components/linkButton.tsx';
import { NavBar, NavBarRef } from '../../components/navBar.tsx';
import { OperationDialog } from '../../components/operation-dialog/operationDialog.tsx';
import { OperationsTable } from '../../components/operation-table/operationsTable.tsx';
import { PageTitle } from '../../components/pageTitle.tsx';
import { ScrapCloseDialog } from '../../components/scrapCloseDialog.tsx';
import { ScrapReviewDialog } from '../../components/scrapReviewDialog.tsx';
import { OperationWithLabel } from '../../types/sharedTypeImpl.ts';
import { trpc } from '../../utils/trpc/trpc.ts';
import { revertOperation } from './revertOperation.ts';
import { useFetchOperations } from './useFetchOperations.tsx';


export function PotentialScraps() {
    const [operationUnderEdit, setOperationUnderEdit] = useState<OperationWithLabel | null>(null);
    const [operationUnderScrapReview, setOperationUnderScrapReview] = useState<OperationWithLabel | null>(null);
    const [operationUnderScrapClose, setOperationUnderScrapClose] = useState<OperationWithLabel | null>(null);
    const [showHandledScrapsDialog, setShowHandledScrapsDialog] = useState(false);
    const navBarRef = useRef<NavBarRef>(null);

    const {
        newOperations,
        intermediateOperations,
        handledOperations,
        refetchOperations,
    } = useFetchOperations();

    const { data: isAdmin } = trpc.user.isAdmin.useQuery(
        undefined,
        { refetchOnWindowFocus: false }
    );


    const handleOperationEditClick = (operation: OperationWithLabel) => {
        if (operation.scrapCloseForm) {
            setOperationUnderScrapClose(operation);
        } else if (operation.scrapReview) {
            setOperationUnderScrapReview(operation);
        } else {
            setOperationUnderEdit(operation);
        }
    }

    const handleScrapReviewClick = (operation: OperationWithLabel) => {
        setOperationUnderScrapReview(operation);
    }

    const handleScrapCloseClick = (operation: OperationWithLabel) => {
        setOperationUnderScrapClose(operation);
    }

    const handleOperationRevertClick = async (operation: OperationWithLabel) => {
        await revertOperation(operation, () => {
            refetchOperations();
            navBarRef.current?.refetchPageIssues();
            toast.success('Operation reverted.');
        });
    }

    const onOperationSaved = () => {
        refetchOperations();
        navBarRef.current?.refetchPageIssues();
    }


    return (
        <>
            <PageTitle title='Scraps' />
            <NavBar ref={navBarRef} />
            <div className='content'>
                {newOperations == null
                    ? 'Loading...'
                    : <>
                        <h2>Pending potential scraps</h2>
                        <OperationsTable
                            narrow={false}
                            operations={newOperations}
                            showBatch={true}
                            operationTypeIndicators={false}
                            highlightPotentialScraps={false}
                            showScrapLabels={true}
                            showScrapReasons={false}
                            showOrderNumbers={true}
                            enableScrapReview={!!isAdmin}
                            enableScrapClose={false}
                            showEditColumn={true}
                            showScrapCloseValues={false}
                            hideEditIfReviewedOrClosed={true}
                            enableRevert={false}
                            onEditClick={handleOperationEditClick}
                            onScrapReviewClick={handleScrapReviewClick}
                            onScrapCloseClick={handleScrapReviewClick}
                        />
                    </>
                }

                {intermediateOperations == null
                    ? 'Loading...'
                    : <>
                        <h2 style={{ marginTop: '40px' }}>Inspection queue</h2>
                        <OperationsTable
                            narrow={false}
                            operations={intermediateOperations}
                            showBatch={true}
                            operationTypeIndicators={false}
                            highlightPotentialScraps={false}
                            showScrapLabels={true}
                            showScrapReasons={false}
                            showOrderNumbers={true}
                            enableScrapReview={false}
                            enableScrapClose={true}
                            showEditColumn={!!isAdmin}
                            showScrapCloseValues={true}
                            hideEditIfReviewedOrClosed={false}
                            enableRevert={true}
                            onEditClick={handleOperationEditClick}
                            onScrapCloseClick={handleScrapCloseClick}
                            onRevertClick={handleOperationRevertClick}
                        />
                    </>
                }

                {handledOperations && <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
                        <h2 style={{ marginTop: '40px' }}>Handled potential scraps</h2>
                        <LinkButton
                            style={{ marginRight: '12px' }}
                            onClick={() => setShowHandledScrapsDialog(true)}
                            children='History'
                        />
                    </div>
                    <OperationsTable
                        narrow={false}
                        operations={handledOperations}
                        showBatch={true}
                        operationTypeIndicators={false}
                        highlightPotentialScraps={false}
                        showScrapLabels={false}
                        showScrapReasons={true}
                        showOrderNumbers={true}
                        enableScrapReview={false}
                        enableScrapClose={false}
                        showScrapCloseValues={true}
                        showEditColumn={true}
                        hideEditIfReviewedOrClosed={false}
                        enableRevert={true}
                        onEditClick={handleOperationEditClick}
                        onRevertClick={handleOperationRevertClick}
                    />
                </>
                }
            </div >

            <OperationDialog
                isOpen={operationUnderEdit != null}
                operationUnderEdit={operationUnderEdit}
                onClose={() => setOperationUnderEdit(null)}
                onOperationSaved={onOperationSaved}
            />
            {operationUnderScrapReview &&
                <ScrapReviewDialog
                    isOpen={true}
                    operation={operationUnderScrapReview}
                    onClose={() => setOperationUnderScrapReview(null)}
                    onSuccess={onOperationSaved}
                />
            }
            {operationUnderScrapClose &&
                <ScrapCloseDialog
                    isOpen={true}
                    operation={operationUnderScrapClose}
                    onClose={() => setOperationUnderScrapClose(null)}
                    onSuccess={onOperationSaved}
                />
            }
            {showHandledScrapsDialog &&
                <HandledScrapsDialog onClose={() => setShowHandledScrapsDialog(false)} />
            }
        </>
    )
}
